import { useCallback, useEffect, useRef, useState } from 'react';
import { chatService, DM_READ_EVENT } from '../services/chat';
import { useMessagesRealtime } from './useMessagesRealtime';

/** Total unread DM count for the nav badge. */
export function useUnreadMessages(enabled: boolean, currentUserId?: string) {
  const [unreadCount, setUnreadCount] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setUnreadCount(0);
      return;
    }
    try {
      const count = await chatService.getUnreadCount();
      if (mountedRef.current) setUnreadCount(count);
    } catch {
      // Badge is non-critical; stay silent.
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Count locally as messages land; the read RPC below re-syncs the truth.
  useMessagesRealtime(
    enabled,
    message => {
      if (message.sender_id === currentUserId) return;
      setUnreadCount(count => count + 1);
    },
    refresh
  );

  // Reads happen via RPC (an UPDATE we don't subscribe to), so recount then.
  useEffect(() => {
    if (!enabled) return;
    const handler = () => refresh();
    window.addEventListener(DM_READ_EVENT, handler);
    return () => window.removeEventListener(DM_READ_EVENT, handler);
  }, [enabled, refresh]);

  return { unreadCount, refresh };
}
